import {
  EXPORT_STORAGE_LIMIT_BYTES,
  UserExportRecord,
} from '@/constants/userExports';

const DB_NAME = 'aluna-user-exports';
const STORE = 'exports';

interface LocalExportRow {
  id: string;
  name: string;
  fileType: string;
  sizeBytes: number;
  createdAt: string;
  sourceTool?: string;
  blob: Blob;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function rowToRecord(row: LocalExportRow): UserExportRecord {
  return {
    id: row.id,
    name: row.name,
    fileType: row.fileType,
    sizeBytes: row.sizeBytes,
    createdAt: row.createdAt,
    storageKey: row.id,
    sourceTool: row.sourceTool,
    isLocal: true,
  };
}

export async function listLocalExports(): Promise<UserExportRecord[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const request = store.getAll();
    request.onsuccess = () => {
      const rows = (request.result as LocalExportRow[]) || [];
      resolve(
        rows
          .map(rowToRecord)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      );
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

export async function getLocalExportUsedBytes(): Promise<number> {
  const items = await listLocalExports();
  return items.reduce((sum, item) => sum + item.sizeBytes, 0);
}

export async function getLocalExportBlob(id: string): Promise<Blob | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(id);
    request.onsuccess = () => {
      const row = request.result as LocalExportRow | undefined;
      resolve(row?.blob ?? null);
    };
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

export async function addLocalExport(params: {
  blob: Blob;
  fileName: string;
  fileType: string;
  sourceTool?: string;
}): Promise<UserExportRecord> {
  const used = await getLocalExportUsedBytes();
  if (used + params.blob.size > EXPORT_STORAGE_LIMIT_BYTES) {
    throw new Error('Quota de 1 Mo atteint. Supprimez des exports ou connectez-vous.');
  }

  const row: LocalExportRow = {
    id: crypto.randomUUID(),
    name: params.fileName,
    fileType: params.fileType,
    sizeBytes: params.blob.size,
    createdAt: new Date().toISOString(),
    sourceTool: params.sourceTool,
    blob: params.blob,
  };

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();

  return rowToRecord(row);
}

export async function deleteLocalExport(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
